import React, {useRef, useState, useEffect, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  Share,
  Alert,
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from 'react-native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {RouteProp} from '@react-navigation/native';
import Sound from 'react-native-sound';
import {NativeModules} from 'react-native';
import Tts from 'react-native-tts';
const {TempFileWriter} = NativeModules;
import {useTheme} from '../hooks/useTheme';
import {Header} from '../components/Header';
import {Comments} from '../components/Comments';
import {Icon} from '../components/Icon';
import {Article} from '../types/article';
import {ArticleContentWithAds} from '../components/article/ArticleContentWithAds';
import {AdBanner} from '../components/AdBanner';
import {BackToTopButton} from '../components/BackToTopButton';
import {fetchArticle} from '../services/api';
import {API_CONFIG} from '../services/config';
import {fetchArticleAds, ArticleAds} from '../services/adService';
import {ActivityIndicator} from 'react-native';

// Enable playback in silent mode on iOS
Sound.setCategory('Playback');

type RootStackParamList = {
  Home: undefined;
  Article: {article: Article; index: number; articles: Article[]};
};

type ArticleScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Article'>;
  route: RouteProp<RootStackParamList, 'Article'>;
};

export const ArticleScreen: React.FC<ArticleScreenProps> = ({
  navigation,
  route,
}) => {
  const {article: initialArticle, index, articles} = route.params;
  const {colors} = useTheme();
  const scrollViewRef = useRef<ScrollView>(null);
  const commentsRef = useRef<View>(null);
  
  const [article, setArticle] = useState<Article>(initialArticle);
  const [isLoadingContent, setIsLoadingContent] = useState(!initialArticle.content);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [commentsY, setCommentsY] = useState(0);
  const [headerAd, setHeaderAd] = useState<ArticleAds['header']>(null);
  const [headerAdLoaded, setHeaderAdLoaded] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Audio player ref and temp file path
  const soundRef = useRef<Sound | null>(null);
  const tempAudioPath = useRef<string | null>(null);
  const pausedPosition = useRef<number>(0);
  const pulseAnim = useRef(new Animated.Value(1)).current;

  // Pulse animation when playing
  useEffect(() => {
    if (isSpeaking) {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 0.6,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ]),
      );
      loop.start();
      return () => loop.stop();
    } else {
      pulseAnim.setValue(1);
    }
  }, [isSpeaking, pulseAnim]);

  const SCROLL_THRESHOLD = 300;

  // Track scroll position for back-to-top button
  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    setShowBackToTop(offsetY > SCROLL_THRESHOLD);
  }, []);

  const handleBackToTop = useCallback(() => {
    scrollViewRef.current?.scrollTo({y: 0, animated: true});
  }, []);

  useEffect(() => {
    const loadHeaderAd = async () => {
      const ads = await fetchArticleAds(0);
      setHeaderAd(ads.header);
      setHeaderAdLoaded(true);
    };
    loadHeaderAd();
  }, []);

  // Fetch full content if missing
  React.useEffect(() => {
    if (!article.content) {
      const loadFullArticle = async () => {
        try {
          setIsLoadingContent(true);
          const fullArticle = await fetchArticle(article.id);
          setArticle(prev => ({...prev, ...fullArticle}));
        } catch (error) {
          console.error('Failed to load full article:', error);
        } finally {
          setIsLoadingContent(false);
        }
      };
      loadFullArticle();
    } else {
        setIsLoadingContent(false);
    }
  }, [article.id, article.content]);

  // Cleanup sound and temp file on unmount
  useEffect(() => {
    if (Platform.OS === 'android') {
      Tts.setDefaultLanguage('it-IT');
      Tts.setDefaultRate(0.5);
    }
    return () => {
      if (Platform.OS === 'android') {
        Tts.stop();
      }
      if (soundRef.current) {
        soundRef.current.stop();
        soundRef.current.release();
        soundRef.current = null;
      }
      if (tempAudioPath.current && Platform.OS === 'ios' && TempFileWriter) {
        TempFileWriter.deleteTempFile(tempAudioPath.current).catch(() => {});
        tempAudioPath.current = null;
      }
    };
  }, []);

  const hasPrev = index > 0;
  const hasNext = articles && index < articles.length - 1;

  const cleanupAudio = () => {
    if (Platform.OS === 'android') { Tts.stop(); }
    if (soundRef.current) { soundRef.current.stop(); soundRef.current.release(); soundRef.current = null; }
    if (tempAudioPath.current && Platform.OS === 'ios' && TempFileWriter) { TempFileWriter.deleteTempFile(tempAudioPath.current).catch(() => {}); tempAudioPath.current = null; }
    pausedPosition.current = 0;
  };

  const handlePrevArticle = () => {
    if (hasPrev && articles) {
      cleanupAudio();
      const prevIndex = index - 1;
      navigation.replace('Article', {
        article: articles[prevIndex],
        index: prevIndex,
        articles,
      });
    }
  };

  const handleNextArticle = () => {
    if (hasNext && articles) {
      cleanupAudio();
      const nextIndex = index + 1;
      navigation.replace('Article', {
        article: articles[nextIndex],
        index: nextIndex,
        articles,
      });
    }
  };

  const handleShare = async () => {
    try {
      await Share.share({
        message: `${article.name}\n\n${article.url}`,
        title: article.name,
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleScrollToComments = () => {
    if (commentsY > 0) {
      scrollViewRef.current?.scrollTo({y: commentsY - 100, animated: true});
    }
  };

  // Helper to play from a file path at a given position
  const playFromFile = (filePath: string, fromPosition: number = 0) => {
    const sound = new Sound(filePath, '', (error) => {
      if (error) {
        console.log('Sound load error:', error);
        setIsSpeaking(false);
        setIsPaused(false);
        return;
      }
      
      soundRef.current = sound;
      
      if (fromPosition > 0) {
        sound.setCurrentTime(fromPosition);
      }
      
      sound.play((success) => {
        if (success) {
          setIsSpeaking(false);
          setIsPaused(false);
          pausedPosition.current = 0;
        }
      });
    });
  };

  // Toggle play/pause using Google TTS (iOS) or native TTS (Android)
  const handleAscolta = async () => {
    const plainText = article.content
      ? article.content
          .replace(/<\/(h[1-6]|p|div|li|blockquote)>/gi, '. ')
          .replace(/<br\s*\/?>/gi, '. ')
          .replace(/<[^>]*>/g, ' ')
          .replace(/\.(\s*\.)+/g, '.')
          .replace(/\s+/g, ' ')
          .trim()
      : '';

    if (!plainText || plainText.length < 10) {
      Alert.alert('Debug', `Content length: ${plainText.length}, has content: ${!!article.content}`);
      return;
    }

    // ---- Android: use device native TTS ----
    if (Platform.OS === 'android') {
      if (isSpeaking) {
        Tts.stop();
        setIsSpeaking(false);
        setIsPaused(false);
        return;
      }

      setIsSpeaking(true);
      setIsPaused(false);

      const onFinish = () => {
        setIsSpeaking(false);
        setIsPaused(false);
      };
      Tts.addEventListener('tts-finish', onFinish);
      Tts.addEventListener('tts-cancel', onFinish);

      try {
        await Tts.speak(plainText.slice(0, 4000));
      } catch (error) {
        console.log('TTS error:', error);
        setIsSpeaking(false);
      }
      return;
    }

    // ---- iOS: use Google Cloud TTS + TempFileWriter ----
    // If paused, resume from saved position
    if (isPaused && tempAudioPath.current) {
      setIsPaused(false);
      setIsSpeaking(true);
      if (soundRef.current) {
        soundRef.current.stop();
        soundRef.current.release();
        soundRef.current = null;
      }
      playFromFile(tempAudioPath.current, pausedPosition.current);
      return;
    }

    // If playing, save position and stop
    if (isSpeaking && soundRef.current) {
      soundRef.current.getCurrentTime((seconds) => {
        pausedPosition.current = seconds;
        soundRef.current?.stop();
        soundRef.current?.release();
        soundRef.current = null;
        setIsPaused(true);
        setIsSpeaking(false);
      });
      return;
    }

    setIsSpeaking(true);
    setIsPaused(false);
    pausedPosition.current = 0;
    
    try {
      const ttsUrl = `${API_CONFIG.API_URL}/app/tts/synthesize`;
      const bodyJson = JSON.stringify({text: plainText.slice(0, 5000)});
      const fileName = `tts_audio_${Date.now()}.mp3`;

      const filePath = await TempFileWriter.downloadToTempFile(ttsUrl, bodyJson, fileName);
      tempAudioPath.current = filePath;

      playFromFile(filePath);
    } catch (error) {
      console.log('TTS error:', error);
      setIsSpeaking(false);
      setIsPaused(false);
    }
  };

  // Stop and reset audio
  const handleStop = () => {
    cleanupAudio();
    setIsSpeaking(false);
    setIsPaused(false);
  };

  const getReadingTime = () => {
    if (!article.content) return 1;
    const plainText = article.content.replace(/<[^>]*>/g, ' ');
    const wordCount = plainText.split(/\s+/).filter(w => w.length > 0).length;
    return Math.max(1, Math.ceil(wordCount / 200));
  };

  const dynamicStyles = {
    container: {
      backgroundColor: colors.surface,
    },
    title: {
      color: colors.textPrimary,
    },
    categoryBadge: {
      backgroundColor: colors.primaryPastel,
    },
    categoryText: {
      color: colors.primary,
    },
    meta: {
      color: colors.textSecondary,
    },
    author: {
      color: colors.primary,
    },
  };

  return (
    <View style={[styles.container, dynamicStyles.container]}>
      {/* Header with action bar showing back + browser buttons */}
      <Header
        showActionBar
        onBackPress={() => navigation.goBack()}
        articleUrl={article.url}
      />

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}>
        
        {/* Header Ad - Above everything (placeholder only while loading) */}
        <AdBanner
          position="top"
          imageUrl={headerAd?.image_url || undefined}
          targetUrl={headerAd?.target_url || undefined}
          showPlaceholder={!headerAdLoaded}
        />
        
        {/* Article Header */}
        <View style={styles.header}>
          {article.category && (
            <View style={[styles.categoryBadge, dynamicStyles.categoryBadge]}>
              <Text style={[styles.categoryText, dynamicStyles.categoryText]}>
                {article.category.toUpperCase()}
              </Text>
            </View>
          )}

          <Text style={[styles.title, dynamicStyles.title]}>{article.name}</Text>

          <View style={styles.meta}>
            <Text style={[styles.author, dynamicStyles.author]}>{article.author}</Text>
            <Text style={[styles.separator, dynamicStyles.meta]}>•</Text>
            <Text style={[styles.date, dynamicStyles.meta]}>{article.formatted_date}</Text>
            <Text style={[styles.separator, dynamicStyles.meta]}>•</Text>
            <Text style={[styles.readTime, dynamicStyles.meta]}>{getReadingTime()} min lettura</Text>
          </View>

          {/* Action Bar - Ascolta, Share, Comment */}
          <View style={styles.actionBar}>
            {/* Ascolta + Stop grouped together */}
            <View style={styles.ascoltaGroup}>
              {/* Ascolta/Pause Button */}
              <Animated.View style={{opacity: isSpeaking ? pulseAnim : 1}}>
                <TouchableOpacity 
                  style={[
                    styles.ascoltaButton, 
                    {backgroundColor: (isSpeaking || isPaused) ? '#E53935' : colors.primary}
                  ]} 
                  onPress={handleAscolta}
                  activeOpacity={0.8}
                >
                  <Icon 
                    name={isSpeaking ? 'pause' : 'volume-down'} 
                    size={16} 
                    color="#FFFFFF" 
                  />
                  <Text style={styles.ascoltaText}>
                    {isSpeaking ? 'Pausa' : isPaused ? 'Riprendi' : 'Ascolta'}
                  </Text>
                </TouchableOpacity>
              </Animated.View>

              {/* Stop Button - only show when playing or paused */}
              {(isSpeaking || isPaused) && (
                <TouchableOpacity 
                  style={[styles.stopButton, {backgroundColor: '#9E9E9E'}]} 
                  onPress={handleStop}
                  activeOpacity={0.8}
                >
                  <Icon name="stop" size={14} color="#FFFFFF" />
                </TouchableOpacity>
              )}
            </View>

            {/* Right side buttons */}
            <View style={styles.rightButtons}>
              {/* Share Button */}
              <TouchableOpacity 
                style={[styles.roundButton, {backgroundColor: colors.primaryPastel}]} 
                onPress={handleShare}
                activeOpacity={0.7}
              >
                <Icon name="share-nodes" size={18} color={colors.primary} />
              </TouchableOpacity>

              {/* Comment Button */}
              <TouchableOpacity 
                style={[styles.roundButton, {backgroundColor: colors.primaryPastel}]} 
                onPress={handleScrollToComments}
                activeOpacity={0.7}
              >
                <Icon name="comment" size={18} color={colors.primary} />
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Hero image - hidden when show_cover is false */}
        {article.show_cover !== false && article.image && (
          <Image
            source={{uri: article.image}}
            style={styles.heroImage}
            resizeMode="cover"
          />
        )}

        <View style={[styles.content, article.show_cover === false && {paddingTop: 0}]}>
          {isLoadingContent ? (
            <ActivityIndicator size="large" color={colors.primary} style={{marginTop: 20}} />
          ) : article.content ? (
            <ArticleContentWithAds 
              content={article.content}
              galleries={article.galleries}
              showCover={article.show_cover}
            />
          ) : (
            <Text style={[styles.noContent, dynamicStyles.meta]}>Contenuto non disponibile</Text>
          )}
        </View>

        {/* Comments Section */}
        <View 
          ref={commentsRef}
          onLayout={(event) => {
            setCommentsY(event.nativeEvent.layout.y);
          }}
        >
          <Comments slug={article.slug} />
        </View>

        {/* Extra padding for floating nav bar */}
        <View style={{height: 60}} />
      </ScrollView>

      {/* Floating Navigation Bar */}
      {(hasPrev || hasNext) && (
        <View style={[styles.navBar, {backgroundColor: colors.surface, borderTopColor: colors.border}]}>
          <TouchableOpacity 
            style={[styles.navButton, !hasPrev && styles.navButtonDisabled]}
            onPress={handlePrevArticle}
            disabled={!hasPrev}
          >
            <Icon name="chevron-left" size={20} color={hasPrev ? colors.primary : colors.textSecondary} />
            <Text style={[styles.navButtonText, {color: hasPrev ? colors.primary : colors.textSecondary}]}>
              Precedente
            </Text>
          </TouchableOpacity>

          <View style={[styles.navDivider, {backgroundColor: colors.border}]} />

          <TouchableOpacity 
            style={[styles.navButton, !hasNext && styles.navButtonDisabled]}
            onPress={handleNextArticle}
            disabled={!hasNext}
          >
            <Text style={[styles.navButtonText, {color: hasNext ? colors.primary : colors.textSecondary}]}>
              Successivo
            </Text>
            <Icon name="chevron-right" size={20} color={hasNext ? colors.primary : colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}
      <BackToTopButton
        visible={showBackToTop}
        onPress={handleBackToTop}
        bottomOffset={(hasPrev || hasNext) ? 120 : 40}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  header: {
    padding: 16,
  },
  categoryBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 20,
    marginBottom: 12,
  },
  categoryText: {
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    lineHeight: 28,
    marginBottom: 12,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  author: {
    fontSize: 13,
    fontWeight: '600',
  },
  separator: {
    marginHorizontal: 6,
  },
  date: {
    fontSize: 13,
  },
  readTime: {
    fontSize: 13,
  },
  // Action Bar
  actionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  ascoltaGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  ascoltaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 25,
    gap: 6,
  },
  ascoltaText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  stopButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  roundButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroImage: {
    width: '100%',
    aspectRatio: 16 / 9,
  },
  content: {
    padding: 16,
  },
  noContent: {
    fontSize: 16,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 20,
  },
  navBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    paddingBottom: 22, // Extra safe area padding
    borderTopWidth: 1,
  },
  navButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    justifyContent: 'center',
  },
  navButtonDisabled: {
    opacity: 0.5,
  },
  navButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  navDivider: {
    width: 1,
    height: 24,
  },
});
